import { useCallback, useMemo, useRef } from 'react';

import {
    type AuditFilterChange,
    type AuditLogFilters,
    applyAuditFilterChange,
    parseAuditLogFilters,
    serializeAuditLogFilters,
} from '@/domain/auditLog';
import useQueryParams from '@/hooks/useQueryParams';

/** `type` is a typed value once the typing pauses; `commit` is the field losing focus. */
export type AuditTypingStep = 'type' | 'commit';

export interface AuditLogFilterState {
    /** The filters the URL holds, with the default view exclusion unless `showViews=1`. */
    filters: AuditLogFilters;
    /** A pick, a toggle, a chip removal or "Clear all": one history entry, so Back undoes it. */
    setFilters: (next: AuditLogFilters) => void;
    /** A click on a value or a pivot, which also get a history entry each. */
    applyChange: (change: AuditFilterChange) => void;
    /**
     * Typed text. The first report of a typing session adds a history entry, later ones
     * replace it, so Back skips the half-typed values and returns to where the typing
     * started. A commit ends the session.
     */
    typeFilters: (next: AuditLogFilters, step: AuditTypingStep) => void;
}

function urlOf(filters: AuditLogFilters): string {
    return JSON.stringify(serializeAuditLogFilters(filters));
}

/**
 * The URL is the one source of truth, so a link, a reload or Back reproduces exactly the
 * list and the export, and nothing on the page can show filters other than those applied.
 */
export function useAuditLogFilterState(): AuditLogFilterState {
    const [query, setQueryParams] = useQueryParams();
    const filters = useMemo(() => parseAuditLogFilters(query), [query]);
    // The URL the current typing session last wrote. While the URL still shows it, the
    // session owns the history entry and may replace it.
    const typingUrl = useRef<string | null>(null);

    const write = useCallback(
        (next: AuditLogFilters, step: 'push' | AuditTypingStep) => {
            const current = urlOf(filters);
            const target = urlOf(next);
            const ownsEntry = typingUrl.current === current;
            if (target !== current) {
                // Every owned key is written, removed ones as null, because the hook merges.
                setQueryParams(serializeAuditLogFilters(next), {
                    replace: step !== 'push' && ownsEntry,
                });
            }
            const typing = step === 'type' && (target !== current || ownsEntry);
            typingUrl.current = typing ? target : null;
        },
        [filters, setQueryParams]
    );

    const setFilters = useCallback((next: AuditLogFilters) => write(next, 'push'), [write]);
    const applyChange = useCallback(
        (change: AuditFilterChange) => write(applyAuditFilterChange(filters, change), 'push'),
        [filters, write]
    );
    const typeFilters = useCallback(
        (next: AuditLogFilters, step: AuditTypingStep) => write(next, step),
        [write]
    );

    return { filters, setFilters, applyChange, typeFilters };
}
